/**
 * The multi-line Input. Same surface (--card, never --popover — that is the dock, and Tuxedo's
 * dock is midnight in both modes), same focus ring, same disabled fill — only the height and the
 * alignment differ, which is exactly what shadcn's own Textarea is.
 */
import { useFieldEdge } from '@/lib/edges';
import { familyFor } from '@/lib/fonts';
import { cn } from '@/lib/utils';
import { Platform, TextInput } from 'react-native';

function Textarea({ className, ...props }: React.ComponentProps<typeof TextInput> & React.RefAttributes<TextInput>) {
  // A field the user types into is the one place a fallback font is most obvious, because it
  // sits next to a label that is already in the right one. Native needs the family by name.
  const fontFamily = familyFor('sans', 400);
  // --input against --card is 1.75:1 in the dark rooms, which is a field with no edge at all.
  const edge = useFieldEdge();
  return (
    <TextInput
      multiline
      textAlignVertical="top"
      className={cn(
        'bg-card text-foreground text-body font-sans min-h-20 w-full rounded-md border px-3 py-2',
        edge,
        props.editable === false && cn('bg-muted text-muted-foreground', Platform.select({ web: 'cursor-not-allowed' })),
        Platform.select({
          web: cn(
            'placeholder:text-muted-foreground/70 selection:bg-accent field-sizing-content outline-none transition-[color,box-shadow]',
            'focus-visible:border-ring focus-visible:ring-accent focus-visible:ring-[3px]',
          ),
          native: 'placeholder:text-muted-foreground/70',
        }),
        className,
      )}
      {...props}
      style={fontFamily ? [{ fontFamily }, props.style] : props.style}
    />
  );
}

export { Textarea };
